import React, { useState, useEffect, useRef } from "react";
import "./RecWiseCode.css";
import { useNavigate } from 'react-router-dom';
import CTA from './CTA';

const ApprovalWorkflowModule = () => {
  const [activeSection, setActiveSection] = useState("workflow-configuration");
  const [visibleSections, setVisibleSections] = useState(new Set());
  const sectionsRef = useRef({});
  const navigate = useNavigate();
  const handleApprovals = () => navigate('/access-recruit');

  const sectionContent = {
    "workflow-configuration": {
      title: "Flexible Workflow",
      description: "Design and configure approval workflows with intuitive drag-and-drop interface for any HR process. Create multi-level approval chains, parallel approvals, and conditional routing based on organizational hierarchies, cost thresholds, and policy requirements. Configure escalation rules, delegation settings, and approval timeouts. Design workflows for expense approvals, leave requests, promotions, transfers, policy changes, and budget approvals with tailored approval logic for each process type."
    },
    "smart-routing": {
      title: "Intelligent Approval Routing",
      description: "Automate approval routing with smart logic that identifies the right approvers based on organizational structure, role hierarchies, and conditional criteria. Implement dynamic routing that adapts to organizational changes, temporary assignments, and approval delegation. Configure escalation paths for overdue approvals and exception handling for special cases. Ensure approvals reach the correct decision-makers while maintaining audit trails and compliance with approval policies across all HR processes."
    },
    "approval-dashboard": {
      title: "Unified Approval Dashboard",
      description: "Provide centralized visibility into all approval requests through comprehensive dashboards for both approvers and requestors. Track pending approvals, approval history, and decision timelines across all workflows. Configure personalized views based on role, department, and approval responsibilities. Implement notifications, reminders, and mobile alerts for time-sensitive approvals. Enable bulk approvals, delegation settings, and approval analytics for efficient approval management and decision-making."
    },
    "mobile-approvals": {
      title: "Mobile-First Approval",
      description: "Enable approval decisions on-the-go with mobile-optimized approval interfaces and push notifications. Approve requests, review supporting documents, and make decisions from smartphones and tablets. Implement offline approval capabilities with synchronization when connectivity is restored. Provide approvers with context-rich information, historical data, and decision support tools on mobile devices. Track mobile approval rates, response times, and user satisfaction for mobile approval experiences."
    },
    "approval-analytics": {
      title: "Approval Analytics",
      description: "Gain insights into approval efficiency, bottlenecks, and decision patterns with comprehensive analytics. Track approval cycle times, approval rates, escalation frequencies, and decision trends. Identify approval bottlenecks, process inefficiencies, and optimization opportunities. Generate approval reports, compliance audits, and performance dashboards. Use predictive analytics to forecast approval volumes, optimize approval workflows, and improve decision-making efficiency across the organization."
    }
  };

  const sections = [
    { id: "workflow-configuration", label: "CONFIGURATION" },
    { id: "smart-routing", label: "ROUTING" },
    { id: "approval-dashboard", label: "DASHBOARD" },
    { id: "mobile-approvals", label: "MOBILE" },
    { id: "approval-analytics", label: "ANALYTICS" }
  ];

  useEffect(() => {
    const onScroll = () => {
      const pos = window.scrollY + 150;
      for (const sec of sections) {
        const el = sectionsRef.current[sec.id];
        if (!el) continue;
        if (pos >= el.offsetTop && pos < el.offsetTop + el.offsetHeight) {
          setActiveSection(sec.id);
          break;
        }
      }
    };
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((en) => {
          if (en.isIntersecting) {
            setVisibleSections((prev) => new Set([...prev, en.target.dataset.section]));
          }
        });
      },
      { threshold: 0.15 }
    );
    Object.values(sectionsRef.current).forEach((el) => el && obs.observe(el));
    return () => obs.disconnect();
  }, []);

  const scrollToSection = (id) => {
    const el = sectionsRef.current[id];
    if (el) window.scrollTo({ top: el.offsetTop - 100, behavior: "smooth" });
  };

  return (
    <div className="resto-main-wrapper">
      {/* <nav className="resto-sticky-navbar">
        <div className="resto-nav-container">
          {sections.map((s) => (
            <button
              key={s.id}
              className={`resto-nav-button ${activeSection === s.id ? "resto-active" : ""}`}
              onClick={() => scrollToSection(s.id)}
            >
              {s.label}
            </button>
          ))}
        </div>
      </nav> */}

      <div className="resto-content-wrapper">

        {/* 1. Workflow Configuration */}
        <section
          ref={(el) => (sectionsRef.current["workflow-configuration"] = el)}
          data-section="workflow-configuration"
          className={`resto-section ${visibleSections.has("workflow-configuration") ? "resto-visible" : ""}`}
        >
          <div className="resto-section-content resto-fade-in-left">
            <div className="resto-section-label">Configuration</div>
            <h2 className="resto-section-title">
              {sectionContent["workflow-configuration"].title}
            </h2>
            <p className="resto-section-description">
              {sectionContent["workflow-configuration"].description}
            </p>

            <div className="resto-buttons-group">
              <button className="resto-primary-btn" onClick={handleApprovals}>Configure Workflow</button>
              <button className="resto-secondary-btn" onClick={handleApprovals}>Browse Templates</button>
            </div>
          </div>

          <div className="resto-section-image resto-fade-in-right">
            <img src="/images/aps1.png" className="resto-real-image" alt="Workflow Configuration" />
          </div>
        </section>

        {/* 2. Smart Routing */}
        <section
          ref={(el) => (sectionsRef.current["smart-routing"] = el)}
          data-section="smart-routing"
          className={`resto-section resto-reverse ${visibleSections.has("smart-routing") ? "resto-visible" : ""}`}
        >
          <div className="resto-section-content resto-fade-in-right">
            <div className="resto-section-label">Routing</div>
            <h2 className="resto-section-title">
              {sectionContent["smart-routing"].title}
            </h2>
            <p className="resto-section-description">
              {sectionContent["smart-routing"].description}
            </p>

            <div className="resto-buttons-group">
              <button className="resto-primary-btn" onClick={handleApprovals}>Set Routing Rules</button>
              <button className="resto-secondary-btn" onClick={handleApprovals}>Monitor Routing</button>
            </div>
          </div>

          <div className="resto-section-image resto-fade-in-left">
            <img src="/images/aps2.png" className="resto-real-image" alt="Smart Routing" />
          </div>
        </section>

        <CTA/>

        {/* 3. Approval Dashboard */}
        <section
          ref={(el) => (sectionsRef.current["approval-dashboard"] = el)}
          data-section="approval-dashboard"
          className={`resto-section ${visibleSections.has("approval-dashboard") ? "resto-visible" : ""}`}
        >
          <div className="resto-section-content resto-fade-in-left">
            <div className="resto-section-label">Dashboard</div>
            <h2 className="resto-section-title">
              {sectionContent["approval-dashboard"].title}
            </h2>
            <p className="resto-section-description">
              {sectionContent["approval-dashboard"].description}
            </p>

            <div className="resto-buttons-group">
              <button className="resto-primary-btn" onClick={handleApprovals}>Access Dashboard</button>
              <button className="resto-secondary-btn" onClick={handleApprovals}>Customize Views</button>
            </div>
          </div>

          <div className="resto-section-image resto-fade-in-right">
            <img src="/images/aps3.png" className="resto-real-image" alt="Approval Dashboard" />
          </div>
        </section>

        {/* 4. Mobile Approvals */}
        <section
          ref={(el) => (sectionsRef.current["mobile-approvals"] = el)}
          data-section="mobile-approvals"
          className={`resto-section resto-reverse ${visibleSections.has("mobile-approvals") ? "resto-visible" : ""}`}
        >
          <div className="resto-section-content resto-fade-in-right">
            <div className="resto-section-label">Mobile</div>
            <h2 className="resto-section-title">
              {sectionContent["mobile-approvals"].title}
            </h2>
            <p className="resto-section-description">
              {sectionContent["mobile-approvals"].description}
            </p>

            <div className="resto-buttons-group">
              <button className="resto-primary-btn" onClick={handleApprovals}>Mobile Access</button>
              <button className="resto-secondary-btn" onClick={handleApprovals}>Push Notifications</button>
            </div>
          </div>

          <div className="resto-section-image resto-fade-in-left">
            <img src="/images/aps4.png" className="resto-real-image" alt="Mobile Approvals" />
          </div>
        </section>

        {/* 5. Approval Analytics */}
        <section
          ref={(el) => (sectionsRef.current["approval-analytics"] = el)}
          data-section="approval-analytics"
          className={`resto-section ${visibleSections.has("approval-analytics") ? "resto-visible" : ""}`}
        >
          <div className="resto-section-content resto-fade-in-left">
            <div className="resto-section-label">Analytics</div>
            <h2 className="resto-section-title">
              {sectionContent["approval-analytics"].title}
            </h2>
            <p className="resto-section-description">
              {sectionContent["approval-analytics"].description}
            </p>

            <div className="resto-buttons-group">
              <button className="resto-primary-btn" onClick={handleApprovals}>View Analytics</button>
              <button className="resto-secondary-btn" onClick={handleApprovals}>Optimize Workflows</button>
            </div>
          </div>

          <div className="resto-section-image resto-fade-in-right">
            <img src="/images/aps5.png" className="resto-real-image" alt="Approval Analytics" />
          </div>
        </section>

      </div>
    </div>
  );
};

export default ApprovalWorkflowModule;